// src/pages/DonatePage.js

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input, message } from "antd";
import {
  CheckCircleIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
  HeartIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

import "../components/Walletview.css";

const DonatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { wallet, baitcashAccount } = location.state || {};

  const [donationType, setDonationType] = useState("zakat");
  const [donationAmount, setDonationAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  if (!wallet || !baitcashAccount) {
    navigate("/dashboard");
    return null;
  }

  const goBack = () => navigate("/dashboard");

  const handleDonate = async () => {
    setError("");

    if (!donationAmount || Number(donationAmount) <= 0) {
      setError("Please enter a valid donation amount.");
      return;
    }

    setSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSuccess(true);
      message.success(
        `Thank you for donating ${donationAmount} BAIT as ${donationType}!`
      );

      setTimeout(() => {
        navigate("/donation-success", {
          state: {
            message: `Thank you for donating ${donationAmount} BAIT as ${donationType}!`,
          },
        });
      }, 1200);
    } catch (err) {
      setError(err?.message || "Donation failed. Please try again.");
      message.error("Donation failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // ==========================================================
  // STYLES
  // ==========================================================
  const styles = {
    page: {
      width: "100%",
      minHeight: "80vh",
      background: "#faf8f5",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      overflowX: "hidden",
    },

    container: {
      width: "calc(100% - 40px)",
      maxWidth: 540,
      margin: "0 auto 32px",
      padding: "0 0 16px",
      boxSizing: "border-box",
    },

    pageTitle: {
      width: "100%",
      marginBottom: 24,
      boxSizing: "border-box",
    },
    titleWrapper: {
      position: "relative",
      width: "100%",
      minHeight: 52,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
    },
    backButton: {
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: 36,
      height: 36,
      padding: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(154,106,24,0.06)",
      border: "1px solid rgba(154,106,24,0.12)",
      borderRadius: "10px",
      color: "#9a6a18",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxSizing: "border-box",
      flexShrink: 0,
    },
    titleContent: {
      width: "100%",
      textAlign: "center",
      padding: "0 48px",
      boxSizing: "border-box",
    },
       title: {
      margin: "0 0 5px",

      fontFamily:
        "'Inter', 'Poppins', 'Segoe UI', sans-serif",

      fontSize: 26,

      lineHeight: 1.1,

      fontWeight: 600,

      letterSpacing: "1.4px",

      color: "#8a5a1f",

      WebkitTextStroke: "0.4px #6b3d0a",

      textShadow:
        "0 1px 0 rgba(255, 240, 200, 0.6), 0 2px 6px rgba(154, 106, 24, 0.22)",
    },
    subtitle: {
      margin: "4px 0 0",

      fontFamily:
        "'Poppins', 'Segoe UI', sans-serif",

      fontSize: 11,

      fontWeight: 500,

      letterSpacing: "0.6px",

      lineHeight: 1.3,

      color: "#a07a3f",

      opacity: 0.95,
    },

    formContainer: { width: "100%", boxSizing: "border-box" },
    successContainer: {
      width: "100%",
      boxSizing: "border-box",
      textAlign: "center",
    },

    errorBox: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: "#fef3e8",
      color: "#9a5b12",
      border: "1px solid #f5d9b8",
      borderRadius: "10px",
      padding: "8px 12px",
      marginBottom: "16px",
      fontSize: "12px",
      fontWeight: 500,
    },

    card: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: "24px",
      border: "1px solid #ece4d6",
      boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
      marginBottom: "20px",
    },

    formSection: {
      marginBottom: "20px",
    },
    formLabel: {
      display: "block",
      fontSize: "13px",
      fontWeight: 600,
      color: "#5a4a32",
      marginBottom: "6px",
    },
    formSelect: {
      width: "100%",
      height: "48px",
      padding: "0 14px",
      borderRadius: "10px",
      border: "1px solid #e0d6c8",
      fontSize: "15px",
      fontWeight: 500,
      color: "#2a1f12",
      background: "#ffffff",
      outline: "none",
      appearance: "auto",
    },
    formInput: {
      width: "100%",
      height: "48px",
      padding: "0 14px",
      borderRadius: "10px",
      border: "1px solid #e0d6c8",
      fontSize: "15px",
      fontWeight: 500,
      color: "#2a1f12",
      background: "#ffffff",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
      outline: "none",
    },
    formHelper: {
      marginTop: "4px",
      fontSize: "11px",
      color: "#9b8b73",
      lineHeight: 1.4,
    },

    submitButton: {
      width: "100%",
      height: "52px",
      borderRadius: "12px",
      border: "none",
      background: "linear-gradient(135deg, #9a6a18, #c18a32)",
      color: "#ffffff",
      fontSize: "15px",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginTop: "8px",
      boxShadow: "0 4px 16px rgba(154,106,24,0.18)",
    },

    securityNote: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      marginTop: "24px",
      padding: "12px 0",
      borderTop: "1px solid #ece4d6",
      fontSize: "12px",
      color: "#9b8b73",
    },
    securityIcon: { color: "#9a6a18" },

    successIcon: { color: "#4cae5b" },
    successTitle: {
      margin: "14px 0 2px",
      fontSize: "22px",
      fontWeight: 700,
      color: "#2a1f12",
    },
    successSubtitle: { fontSize: "13px", color: "#8a7a62" },
  };

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <div style={styles.page}>
      {/* Main content */}
      <div style={styles.container}>
        <div style={styles.pageTitle}>
          <div style={styles.titleWrapper}>
            {/* BACK BUTTON — uses shared .market-back-btn styles */}
        <button
          type="button"
          className="market-back-btn"
          onClick={goBack}
          title="Back to Dashboard"
          aria-label="Back to Dashboard"
        >
          <ArrowLeftIcon width={18} height={18} />
        </button>
            <div style={styles.titleContent}>
              <h1 style={styles.title}>Donate to Charity</h1>
              <p style={styles.subtitle}>
                Make a difference with your BAIT donation
              </p>
            </div>
          </div>
        </div>

        {success ? (
          // ---------- SUCCESS ----------
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>
              <CheckCircleIcon width={60} height={60} />
            </div>
            <h2 style={styles.successTitle}>Donation Successful</h2>
            <p style={styles.successSubtitle}>
              Thank you for donating {donationAmount} BAIT as{" "}
              {donationType}!
            </p>
            <button onClick={goBack} style={styles.submitButton}>
              Done
            </button>
          </div>
        ) : (
          // ---------- FORM ----------
          <div style={styles.formContainer}>
            {error && (
              <div style={styles.errorBox}>
                <InformationCircleIcon width={18} height={18} />
                <span>{error}</span>
              </div>
            )}

            <div style={styles.card}>
              <div style={styles.formSection}>
                <label style={styles.formLabel}>
                  Donation Type
                </label>
                <select
                  value={donationType}
                  onChange={(e) => {
                    setDonationType(e.target.value);
                    setError("");
                  }}
                  style={styles.formSelect}
                  disabled={submitting}
                >
                  <option value="zakat">Zakat</option>
                  <option value="sadaqah">Sadaqah</option>
                  <option value="waqf">Waqf</option>
                </select>
                <div style={styles.formHelper}>
                  Choose the type of charity donation
                </div>
              </div>

              <div style={styles.formSection}>
                <label style={styles.formLabel}>
                  Amount (BAIT)
                </label>
                <Input
                  type="number"
                  value={donationAmount}
                  onChange={(e) => {
                    setDonationAmount(e.target.value);
                    setError("");
                  }}
                  placeholder="Enter donation amount"
                  disabled={submitting}
                  style={styles.formInput}
                />
                <div style={styles.formHelper}>
                  Amount of BAIT to donate
                </div>
              </div>
            </div>

            <button
              onClick={handleDonate}
              disabled={submitting || !donationAmount}
              style={styles.submitButton}
            >
              {submitting ? (
                <>Processing...</>
              ) : (
                <>
                  Donate Now
                  <HeartIcon width={18} height={18} />
                </>
              )}
            </button>

            <div style={styles.securityNote}>
              <LockClosedIcon
                width={14}
                height={14}
                style={styles.securityIcon}
              />
              <span>Secure • Transparent • 24/7</span>
              <span style={{ marginLeft: "auto", opacity: 0.7 }}>
                Your donations are managed with care and security
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DonatePage;